'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  ArrowRight,
  Camera,
  ChevronRight,
  Images,
  Instagram,
  Loader2,
  MapPin,
  Play,
  Search,
  X,
} from 'lucide-react';
import { useLocale, useFormatter, useTranslations } from 'next-intl';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { Input } from '@/components/ui/input/input';
import { Flag } from '@/components/ui/flag/flag';
import { trpc } from '@/lib/trpc';
import { useRegional } from '@/components/providers/regional-provider';
import { Button } from '@/components/ui/button';
import { type Branding } from '@/lib/branding';
import { buildLocalePath, type Locale } from '@/i18n/config';
import type { MapboxMap } from '@/types/mapbox';

const YOUTUBE_ID = 'x5jilu0foVc';

function YouTubeEmbed({ videoId }: { videoId: string }) {
  const [playing, setPlaying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (playing || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPlaying(true);
          observer.disconnect();
        }
      },
      { threshold: 0.6 },
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [playing]);

  return (
    <div ref={containerRef} className="aspect-video w-full">
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&rel=0&modestbranding=1`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="h-full w-full"
          title="DGR Video"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="group relative h-full w-full cursor-pointer overflow-hidden bg-black"
        >
          <img
            src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
            alt="Play video"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/40">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-dgr-gold transition-transform group-hover:scale-110 sm:h-20 sm:w-20">
              <Play className="h-7 w-7 fill-black text-black sm:h-8 sm:w-8" />
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

// DGR hero image - stored in public/images/dgr/
const HERO_IMAGE_URL = '/images/dgr/HeroImage.jpg';

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || '';

type RideStatus = 'open' | 'almost_full' | 'at_capacity';

interface GlobalRideRow {
  id: string;
  city: string;
  slug: string;
  stateName: string | null;
  stateCode: string | null;
  countryName: string;
  countryCode: string;
  eventDate: string | null;
  startTime: string | null;
  postponedDate?: string | null;
  capacity: string | number | null;
  currentRegistrations: number;
  raised: number;
  donationCount: number;
  currencyCode: string;
  coordinates: string | null;
}

interface CountryEventStat {
  countryCode: string;
  countryName: string;
  ridesCount: number;
  entrantsCount: number;
  totalRaised: number;
  donationCount: number;
  currencyCode: string;
}

interface GlobalRide extends GlobalRideRow {
  countrySlug: string;
  status: RideStatus;
}

function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function parseCapacity(value: string | number | null): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function toRideStatus(currentRegistrations: number, capacity: string | number | null): RideStatus {
  const max = parseCapacity(capacity);
  if (!max) return 'open';
  if (currentRegistrations >= max) return 'at_capacity';
  if (currentRegistrations >= max * 0.85) return 'almost_full';
  return 'open';
}

const DGR_INSTAGRAM_URL = 'https://instagram.com/gentlemansride';

export interface LandingPageProps {
  branding?: Branding;
}

// Replace placeholder images in /public/images/dgr/milestones/ with real milestone photos
const MILESTONES = [
  {
    year: '2012',
    title: 'The First Ride',
    desc: '2,500 riders across 64 cities',
    image: '/images/dgr/milestones/2012.jpg',
    desktop: { x: 6, y: 62 },
    mobile: { x: 65, y: 14 },
    delay: 0.08,
  },
  {
    year: '2015',
    title: 'Global Movement',
    desc: '37,000 riders in 410 cities',
    image: '/images/dgr/milestones/2015.jpg',
    desktop: { x: 24, y: 22 },
    mobile: { x: 20, y: 28 },
    delay: 0.25,
  },
  {
    year: '2019',
    title: 'Record Breaking',
    desc: '116,000 riders — $6.5M raised',
    image: '/images/dgr/milestones/2019.jpg',
    desktop: { x: 46, y: 52 },
    mobile: { x: 70, y: 44 },
    delay: 0.45,
  },
  {
    year: '2022',
    title: 'A Decade of Dapper',
    desc: "$37M raised for men's health",
    image: '/images/dgr/milestones/2022.jpg',
    desktop: { x: 66, y: 18 },
    mobile: { x: 18, y: 60 },
    delay: 0.65,
  },
  {
    year: '2026',
    title: '15 Years of Riding Dapper',
    desc: 'The ride continues — join us',
    image: '/images/dgr/milestones/2026.jpg',
    desktop: { x: 88, y: 38 },
    mobile: { x: 60, y: 78 },
    delay: 0.85,
  },
];

const ROAD_PATH_DESKTOP = 'M-5,80 C10,70 20,40 35,35 C50,30 55,65 70,60 C85,55 90,30 105,25';
const ROAD_PATH_MOBILE =
  'M50,0 C75,10 80,18 70,25 C55,34 25,32 20,42 C15,52 45,55 70,52 C90,50 85,62 65,68 C40,75 30,80 45,90 C55,96 60,100 55,105';

export function LandingPage({ branding }: LandingPageProps) {
  const locale = useLocale() as Locale;
  const t = useTranslations('hero');
  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  const tEvents = useTranslations('events');
  const format = useFormatter();
  const { currency: regionalCurrency } = useRegional();

  const registerHref = buildLocalePath(locale, '/register');
  const donateHref = buildLocalePath(locale, '/donate');

  const heroRef = useRef<HTMLElement>(null);
  const EVENT_DATE = new Date('2026-05-17T00:00:00');
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = Math.max(0, EVENT_DATE.getTime() - now.getTime());
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const rideRef = useRef<HTMLDivElement>(null);
  const bikeRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const milestoneRefs = useRef<(HTMLDivElement | null)[]>([]);
  const badgeRef = useRef<HTMLDivElement>(null);
  const badgeTextRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const ctaSectionRef = useRef<HTMLElement>(null);
  const dgrSectionRef = useRef<HTMLDivElement>(null);
  const statsDesktopRef = useRef<HTMLDivElement>(null);
  const statsMobileRef = useRef<HTMLDivElement>(null);
  const dgrDRef = useRef<SVGGElement>(null);
  const dgrGRef = useRef<SVGGElement>(null);
  const dgrRRef = useRef<SVGGElement>(null);
  const dgr15Ref = useRef<SVGGElement>(null);
  const dgrDapperRef = useRef<SVGGElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Scroll to video section when ?wrapupvideo is present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('wrapupvideo') && videoRef.current) {
      const timer = setTimeout(() => {
        videoRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Globe map state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const mapContainer = useRef<HTMLDivElement>(null);
  const globeMap = useRef<MapboxMap | null>(null);
  const [globeMapLoaded, setGlobeMapLoaded] = useState(false);
  const [globeMapInitialized, setGlobeMapInitialized] = useState(false);

  const { data: countryStats = [] } = trpc.publicLookups.countryEventStats.useQuery({
    currencyCode: regionalCurrency,
  });
  const { data: globalRidesRaw = [] } = trpc.publicLookups.globalMapRides.useQuery({
    currencyCode: regionalCurrency,
  });

  const globalRides = useMemo<GlobalRide[]>(
    () =>
      (globalRidesRaw as GlobalRideRow[]).map((ride) => ({
        ...ride,
        countrySlug: toSlug(ride.countryName),
        status: toRideStatus(ride.currentRegistrations, ride.capacity),
      })),
    [globalRidesRaw],
  );

  const sortedCountries = useMemo(
    () =>
      [...(countryStats as CountryEventStat[])]
        .sort((a, b) => a.countryName.localeCompare(b.countryName))
        .map((c) => ({
          ...c,
          slug: toSlug(c.countryName),
        })),
    [countryStats],
  );

  const filteredRides = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return globalRides
      .filter(
        (ride) =>
          ride.city.toLowerCase().includes(query) ||
          ride.countryName.toLowerCase().includes(query) ||
          (ride.stateName ?? '').toLowerCase().includes(query),
      )
      .slice(0, 8);
  }, [searchQuery, globalRides]);

  const parseCoordinates = useCallback(
    (coords: string | null): { lat: number; lng: number } | null => {
      if (!coords) return null;
      const parts = coords.split(',').map((s) => s.trim());
      if (parts.length !== 2) return null;
      const [lat, lng] = parts.map(Number);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return { lat, lng };
    },
    [],
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load Mapbox GL JS
  useEffect(() => {
    if (!MAPBOX_TOKEN) return;

    const existingStyle = document.querySelector('style[data-mapbox-custom]');
    if (!existingStyle) {
      const style = document.createElement('style');
      style.setAttribute('data-mapbox-custom', 'true');
      style.textContent = `
        .mapboxgl-popup { z-index: 10; }
        .mapboxgl-popup-content {
          padding: 0 !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2) !important;
          overflow: visible;
        }
        .mapboxgl-popup-close-button {
          position: absolute; top: -25px; right: -25px;
          width: 24px; height: 24px; font-size: 18px; line-height: 22px;
          text-align: center; color: #333; background: #1c1917;
          border-radius: 50%; padding: 10px 20px; border: 2px solid white;
          cursor: pointer; transition: all 0.15s ease; z-index: 2;
        }
        .mapboxgl-popup-close-button:hover {
          transform: scale(1.1);
          background-color: transparent !important;
        }
        .mapboxgl-popup-tip { border-top-color: white !important; }
        .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib { display: none !important; }
      `;
      document.head.appendChild(style);
    }

    if (window.mapboxgl) {
      queueMicrotask(() => setGlobeMapLoaded(true));
      return;
    }

    const existingLink = document.querySelector('link[href*="mapbox-gl"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector('script[src*="mapbox-gl"]');
    if (existingScript) {
      if (window.mapboxgl) {
        queueMicrotask(() => setGlobeMapLoaded(true));
      } else {
        existingScript.addEventListener('load', () => setGlobeMapLoaded(true));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.async = true;
    script.onload = () => setGlobeMapLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialize globe map
  useEffect(() => {
    if (!globeMapLoaded || !mapContainer.current || !MAPBOX_TOKEN) return;
    if (globeMap.current) return;

    const mapboxgl = window.mapboxgl;
    if (!mapboxgl) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    const isMd = window.innerWidth >= 768;
    globeMap.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [10, 30],
      zoom: isMd ? 2.2 : 1.6,
      minZoom: isMd ? 1.5 : 1,
      maxZoom: 18,
      projection: 'globe',
    });

    globeMap.current.on('load', () => {
      const m = globeMap.current as unknown as Record<string, (...args: unknown[]) => void>;
      // Cream-tinted oceans
      m.setPaintProperty('water', 'fill-color', '#f5f0e1');
      // Subtle globe atmosphere
      m.setFog({
        color: '#f5f0e1',
        'high-color': '#f5f0e1',
        'horizon-blend': 0.02,
        'space-color': '#faf8f2',
        'star-intensity': 0,
      });
      setGlobeMapInitialized(true);
    });

    return () => {
      if (globeMap.current) {
        globeMap.current.remove();
        globeMap.current = null;
      }
      setGlobeMapInitialized(false);
    };
  }, [globeMapLoaded]);

  // Add clustered GeoJSON source + layers
  useEffect(() => {
    if (!globeMapInitialized || !globeMap.current) return;
    const m = globeMap.current;
    const mapboxgl = window.mapboxgl;

    // Remove old source/layers if they exist
    if (m.getLayer('cluster-count')) m.removeLayer('cluster-count');
    if (m.getLayer('clusters')) m.removeLayer('clusters');
    if (m.getLayer('unclustered-point')) m.removeLayer('unclustered-point');
    if (m.getSource('rides')) m.removeSource('rides');

    const features = globalRides
      .map((ride) => {
        const coords = parseCoordinates(ride.coordinates);
        if (!coords) return null;
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [coords.lng, coords.lat],
          },
          properties: {
            id: ride.id,
            city: ride.city,
            countryName: ride.countryName,
            countryCode: ride.countryCode,
            countrySlug: ride.countrySlug,
            slug: ride.slug,
            stateName: ride.stateName ?? '',
            riders: ride.currentRegistrations,
            raised: ride.raised,
            status: ride.status,
          },
        };
      })
      .filter(Boolean);

    m.addSource('rides', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      } as unknown as import('@/types/mapbox').GeoJSONData,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });

    // Cluster circles — gold shades based on point_count
    m.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'rides',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], '#c9a227', 10, '#a5842e', 30, '#7a6222'],
        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 28, 30, 36],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
      } as Record<string, unknown>,
    });

    // Cluster count labels
    m.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'rides',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 13,
      } as Record<string, unknown>,
      paint: {
        'text-color': '#ffffff',
      } as Record<string, unknown>,
    });

    // Unclustered individual points
    m.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'rides',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#c9a227',
        'circle-radius': 7,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      } as Record<string, unknown>,
    });

    // Click cluster → zoom in
    m.on('click', 'clusters', (e: import('@/types/mapbox').MapboxMapEvent) => {
      const features = m.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      if (!features.length) return;
      const clusterId = features[0].properties?.cluster_id as number;
      const source = m.getSource('rides');
      if (source) {
        source.getClusterExpansionZoom(clusterId, (_err: unknown, zoom: number) => {
          m.flyTo({
            center: features[0].geometry.coordinates as unknown as [number, number],
            zoom,
          });
        });
      }
    });

    // Click unclustered point → show popup
    m.on('click', 'unclustered-point', (e: import('@/types/mapbox').MapboxMapEvent) => {
      const feat = m.queryRenderedFeatures(e.point, {
        layers: ['unclustered-point'],
      });
      if (!feat.length) return;
      const props = feat[0].properties as Record<string, unknown>;
      const coords = feat[0].geometry.coordinates as unknown as [number, number];

      const formattedRaised = format.number(props.raised as number, {
        style: 'currency',
        currency: regionalCurrency,
        notation: 'compact',
        maximumFractionDigits: 0,
      });

      new mapboxgl.Popup({ offset: 15, closeButton: true })
        .setLngLat(coords)
        .setHTML(
          `<div style="padding: 14px; min-width: 220px; font-family: system-ui, sans-serif;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <img src="/flags/${(props.countryCode as string).toUpperCase()}.svg" alt="${
                props.countryName
              }" style="width: 18px; height: 13px; border-radius: 2px; object-fit: cover;" />
              <span style="font-size: 11px; color: #78716c;">${props.countryName}</span>
            </div>
            <h3 style="font-weight: 600; margin: 0 0 4px; font-size: 16px; color: #1c1917;">${
              props.city
            }</h3>
            ${
              props.stateName
                ? `<p style="margin: 0 0 10px; font-size: 12px; color: #78716c;">${props.stateName}</p>`
                : ''
            }
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; margin-bottom: 12px; font-size: 11px;">
              <div>
                <p style="margin: 0; color: #78716c;">${tEvents('riders')}</p>
                <p style="margin: 0; font-weight: 600; color: #1c1917;">${format.number(
                  props.riders as number,
                )}</p>
              </div>
              <div>
                <p style="margin: 0; color: #78716c;">${tEvents('raised')}</p>
                <p style="margin: 0; font-weight: 600; color: hsl(45 67% 38%);">${formattedRaised}</p>
              </div>
            </div>
            <a href="/events/${props.countrySlug}/${
              props.slug
            }" style="display: block; text-align: center; padding: 8px 14px; background: #1c1917; color: white; border-radius: 8px; font-size: 12px; font-weight: 500; text-decoration: none;">${tEvents(
              'viewEvent',
            )}</a>
          </div>`,
        )
        .addTo(m);
    });

    // Pointer cursor on interactive layers
    m.on('mouseenter', 'clusters', () => {
      m.getCanvas().style.cursor = 'pointer';
    });
    m.on('mouseleave', 'clusters', () => {
      m.getCanvas().style.cursor = '';
    });
    m.on('mouseenter', 'unclustered-point', () => {
      m.getCanvas().style.cursor = 'pointer';
    });
    m.on('mouseleave', 'unclustered-point', () => {
      m.getCanvas().style.cursor = '';
    });
  }, [globeMapInitialized, parseCoordinates, globalRides, format, tEvents, regionalCurrency]);

  const flyToRide = useCallback(
    (ride: GlobalRide) => {
      if (!globeMap.current) return;
      const coords = parseCoordinates(ride.coordinates);
      if (!coords) return;
      setSearchQuery('');
      setSearchFocused(false);

      globeMap.current.flyTo({
        center: [coords.lng, coords.lat],
        zoom: 12,
        duration: 2000,
      });
    },
    [parseCoordinates],
  );

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !badgeRef.current || !badgeTextRef.current) return;

    const ctx = gsap.context(() => {
      gsap.set(badgeRef.current, { scaleY: 0, scaleX: 0.8, opacity: 0 });
      gsap.set(badgeTextRef.current, { scaleX: 0, scaleY: 0, opacity: 0 });

      gsap.to(badgeRef.current, {
        scaleY: 1,
        scaleX: 1,
        opacity: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: heroRef.current,
          start: '30% top',
          end: '75% top',
          scrub: 0.6,
        },
      });

      gsap.to(badgeTextRef.current, {
        scaleX: 1,
        scaleY: 1,
        opacity: 1,
        ease: 'back.out(1.6)',
        scrollTrigger: {
          trigger: heroRef.current,
          start: '60% top',
          end: 'bottom top',
          scrub: 0.6,
        },
      });
    });

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      const targets = [statsDesktopRef.current, statsMobileRef.current].filter(Boolean);
      targets.forEach((container) => {
        const items = container!.querySelectorAll('[data-stat]');
        gsap.set(items, { opacity: 0, y: 24 });
        ScrollTrigger.create({
          trigger: container,
          start: 'top 85%',
          once: true,
          onEnter: () => {
            gsap.to(items, {
              opacity: 1,
              y: 0,
              duration: 0.6,
              ease: 'power2.out',
              stagger: 0.12,
            });
          },
        });
      });
    });

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !videoRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        videoRef.current,
        { width: '65%', borderRadius: '1rem' },
        {
          width: '92%',
          borderRadius: '1.5rem',
          ease: 'power2.out',
          scrollTrigger: {
            trigger: videoRef.current,
            start: 'top 90%',
            end: 'top 20%',
            scrub: 0.4,
          },
        },
      );
    });

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !ctaSectionRef.current) return;

    const children = ctaSectionRef.current.querySelectorAll('[data-reveal]');
    if (!children.length) return;

    const ctx = gsap.context(() => {
      gsap.set(children, { y: 40, opacity: 0 });

      gsap.to(children, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.15,
        scrollTrigger: {
          trigger: ctaSectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (
      prefersReducedMotion ||
      !dgrSectionRef.current ||
      !dgrDRef.current ||
      !dgrGRef.current ||
      !dgrRRef.current ||
      !dgr15Ref.current ||
      !dgrDapperRef.current
    )
      return;

    const ctx = gsap.context(() => {
      gsap.set(dgrDRef.current, { x: -150, opacity: 0 });
      gsap.set(dgrRRef.current, { x: 150, opacity: 0 });
      gsap.set(dgrGRef.current, { y: 80, opacity: 0 });
      gsap.set(dgr15Ref.current, {
        scale: 0.5,
        opacity: 0,
        transformOrigin: 'center center',
      });
      gsap.set(dgrDapperRef.current, { y: 15, opacity: 0 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: dgrSectionRef.current,
          start: '80% bottom',
          toggleActions: 'play none none none',
        },
      });

      tl.to(dgrDRef.current, { x: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, 0);
      tl.to(dgrRRef.current, { x: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, 0.1);
      tl.to(dgrGRef.current, { y: 0, opacity: 1, duration: 0.9, ease: 'power3.out' }, 0.2);
      tl.to(dgr15Ref.current, { scale: 1, opacity: 1, duration: 0.7, ease: 'back.out(1.4)' }, 0.5);
      tl.to(dgrDapperRef.current, { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' }, 0.7);
    });

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !rideRef.current || !bikeRef.current || !pathRef.current) return;

    const ctx = gsap.context(() => {
      gsap.to(bikeRef.current, {
        motionPath: {
          path: pathRef.current!,
          align: pathRef.current!,
          alignOrigin: [0.5, 0.5],
          autoRotate: true,
        },
        ease: 'none',
        scrollTrigger: {
          trigger: rideRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1,
        },
      });

      milestoneRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.set(el, { opacity: 0, scale: 0.6, y: 30 });

        const sectionHeight = rideRef.current!.offsetHeight;
        const viewportHeight = window.innerHeight;
        const scrollDistance = sectionHeight - viewportHeight;
        const triggerPx = scrollDistance * MILESTONES[i].delay;

        ScrollTrigger.create({
          trigger: rideRef.current,
          start: 'top top',
          end: 'bottom bottom',
          onUpdate: (self) => {
            const scrolledPx = self.progress * scrollDistance;
            if (scrolledPx >= triggerPx && el.dataset.revealed !== '1') {
              el.dataset.revealed = '1';
              gsap.to(el, {
                opacity: 1,
                scale: 1,
                y: 0,
                duration: 0.8,
                ease: 'back.out(1.7)',
              });
            } else if (scrolledPx < triggerPx && el.dataset.revealed === '1') {
              el.dataset.revealed = '0';
              gsap.to(el, {
                opacity: 0,
                scale: 0.6,
                y: 30,
                duration: 0.4,
                ease: 'power2.in',
              });
            }
          },
        });
      });
    }, rideRef);

    return () => ctx.revert();
  }, [isMobile]);

  return (
    <>
      {/* Hero Section with 80% Height Image */}
      <section
        ref={heroRef}
        className="relative z-10 h-[90vh] min-h-[600px] flex flex-col overflow-visible"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src={HERO_IMAGE_URL}
            alt="Distinguished gentlemen riding classic motorcycles"
            fill
            className="object-cover"
            priority
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex items-center pt-20">
          <div className="container">
            <div className="max-w-2xl">
              {/* Event Date Badge */}
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-sm mb-6">
                {t('eventDate')}
              </div>

              {/* Main Heading */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
                {t('ridingFor')}
                <br />
                <span className="text-dgr-gold">{t('mensHealth')}</span>
              </h1>

              {/* CTA Buttons */}
              <div className="mt-8 flex flex-row gap-4">
                <Button
                  size="lg"
                  asChild
                  className="text-black text-base px-8 bg-dgr-gold w-1/2 sm:w-1/3"
                >
                  <Link href={registerHref} prefetch={false}>
                    {tNav('register')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-white/30 text-black hover:bg-white/10 text-base px-8 w-1/2 sm:w-1/3"
                >
                  <Link href={donateHref} prefetch={false}>
                    {tNav('donate')}
                  </Link>
                </Button>
              </div>

              {/* Partner Logos */}
              <div className="mt-12 flex flex-wrap items-center gap-8">
                <div className="flex flex-col gap-1">
                  <span className="text-white/60 text-xs uppercase mb-2 tracking-wider">
                    {t('proudlySupporting')}
                  </span>
                  <Image
                    src="/images/dgr/sponsors/MovemberLogo.svg"
                    alt="Movember"
                    width={160}
                    height={16}
                    className="h-4 w-auto"
                  />
                </div>
                <div className="h-8 w-px bg-white/20" />
                <div className="flex flex-col gap-1">
                  <span className="text-white/60 text-xs uppercase tracking-wider mb-2">
                    {t('sponsoredBy')}
                  </span>
                  <Image
                    src="/images/dgr/sponsors/TriumphLogo.svg"
                    alt="Triumph Motorcycles"
                    width={142}
                    height={30}
                    className="h-5 w-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* 15 Year Anniversary Badge */}
        <div className="absolute bottom-0 left-1/2 z-20 -translate-x-1/2 translate-y-1/2 flex flex-col items-center">
          <div
            ref={badgeRef}
            className="h-48 w-48 sm:h-60 sm:w-60 md:h-72 md:w-72 lg:h-80 lg:w-80 drop-shadow-[0_8px_32px_rgba(0,0,0,0.7)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1000 690"
              className="w-full h-full"
            >
              <defs>
                <style>{'.st1{fill:#a5842e}'}</style>
              </defs>
              <path
                className="st1"
                d="M137,601.2c68.9-15,71.4,33.8,71.4,33.8,0,0-39.3,29-71.4-33.8"
              />
              <path className="st1" d="M112.5,536.5c70.5,3.1,60.3,51,60.3,51,0,0-45.4,18-60.3-51" />
              <path
                className="st1"
                d="M106.7,465.9c67.5,20.3,46,64.2,46,64.2,0,0-48.5,6.3-46-64.2"
              />
              <path
                className="st1"
                d="M119.7,395.1c59.6,37.7,27.1,74.2,27.1,74.2,0,0-48.4-6.9-27.1-74.2"
              />
              <path
                className="st1"
                d="M145.6,328.9c50.8,48.9,11.6,78.1,11.6,78.1,0,0-46-16.5-11.6-78.1"
              />
              <path
                className="st1"
                d="M219.8,294.1c9.6,69.9-39.2,68.5-39.2,68.5,0,0-25.9-41.4,39.2-68.5"
              />
              <path
                className="st1"
                d="M231,541.6c-57.2,41.3-22.5,75.7-22.5,75.7,0,0,47.9-9.9,22.5-75.7"
              />
              <path
                className="st1"
                d="M208.9,491.1c-60.5,36.2-28.9,73.5-28.9,73.5,0,0,48.5-5.7,28.9-73.5"
              />
              <path
                className="st1"
                d="M200.7,430.3c-63.9,29.7-36.4,70.1-36.4,70.1,0,0,48.9-.7,36.4-70.1"
              />
              <path
                className="st1"
                d="M219.8,362.3c-67.5,20.5-45.8,64.3-45.8,64.3,0,0,48.5,6.2,45.8-64.3"
              />
              <path
                className="st1"
                d="M863.4,601.2c-68.9-15-71.4,33.8-71.4,33.8,0,0,39.3,29,71.4-33.8"
              />
              <path
                className="st1"
                d="M887.9,536.5c-70.5,3.1-60.3,51-60.3,51,0,0,45.4,18,60.3-51"
              />
              <path
                className="st1"
                d="M893.6,465.9c-67.5,20.3-46,64.2-46,64.2,0,0,48.5,6.3,46-64.2"
              />
              <path
                className="st1"
                d="M880.7,395.1c-59.6,37.7-27.1,74.2-27.1,74.2,0,0,48.4-6.9,27.1-74.2"
              />
              <path
                className="st1"
                d="M854.8,328.9c-50.8,48.9-11.6,78.1-11.6,78.1,0,0,46-16.5,11.6-78.1"
              />
              <path
                className="st1"
                d="M780.6,294.1c-9.6,69.9,39.2,68.5,39.2,68.5,0,0,25.9-41.4-39.2-68.5"
              />
              <path
                className="st1"
                d="M769.3,541.6c57.2,41.3,22.5,75.7,22.5,75.7,0,0-47.9-9.9-22.5-75.7"
              />
              <path
                className="st1"
                d="M793.2,491.1c60.5,36.2,28.9,73.5,28.9,73.5,0,0-48.5-5.7-28.9-73.5"
              />
              <path
                className="st1"
                d="M799.6,430.3c63.9,29.7,36.4,70.1,36.4,70.1,0,0-48.9-.7-36.4-70.1"
              />
              <path
                className="st1"
                d="M780.6,362.3c67.5,20.5,45.8,64.3,45.8,64.3,0,0-48.5,6.2-45.8-64.3"
              />
              <path
                className="st1"
                d="M599.4,677.3c-17,0-57-1.9-92.5-17.9v10.7H210.5v-75.7h73.1c3.4,0,11.5-1.1,11.5-10.9v-172h-53.2l.6-73.8,8.6-.8c43.6-4.3,63.5-6.3,82.1-64.8l2.1-6.7h85.3v317.6c0,8,3.9,11.5,13.1,11.5h15.7c-1-5-1.4-10.1-1.4-15.3,0-37.7,27.9-65,66.3-65s61.3,23.9,61.3,55.6-4.6,22.4-13.6,31.9c8.3,1.5,17.8,2.3,27.9,2.3,48.1,0,70-38.4,70-74.1s-6-70.2-61.1-70.2-40.9,14.7-45.6,28.5l-2.2,6.4h-92.4l.4-229.1h286.6l-1.5,90.3h-176.7v34.6c20.9-6.4,41-7.9,63.9-7.9,95.2,0,154.4,54.1,154.4,141.3s-71.4,153.6-186.3,153.6ZM487.7,626.8l14.5,8.6c23.9,14.2,60.2,22.7,97.2,22.7,104.6,0,167.1-50.3,167.1-134.4s-50.6-122.1-135.2-122.1-46.9,1.9-69.9,11.1l-13.2,5.3v-81.6h177l.9-51.9h-247.9l-.3,190.8h60.1c10.6-20,35.9-34.9,60.9-34.9,49.5,0,80.3,34.2,80.3,89.4s-30.6,93.2-89.2,93.2-34.7-1.1-52-8.5l-16.8-7.2,15.4-9.8c13.4-8.5,19.9-17.6,19.9-27.9,0-23.6-21.7-36.4-42.2-36.4s-47.1,18.4-47.1,45.8,1.3,14.6,3.9,21.6l4.8,12.9h-42.1c-19.6,0-32.3-12-32.3-30.6v-298.4h-52.1c-20.8,59.9-48.1,66.4-87.7,70.6l-.3,37.2h53.1v191.2c0,19.8-15.4,30.1-30.6,30.1h-54v37.3h258v-24.2Z"
              />
            </svg>
          </div>
          <div
            ref={badgeTextRef}
            className="-mt-4 sm:-mt-5 md:-mt-6 drop-shadow-[0_4px_16px_rgba(0,0,0,0.7)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="100 685 800 55"
              className="w-48 sm:w-60 md:w-72 lg:w-80 h-auto"
            >
              <path
                fill="#fff"
                d="M134.4,727h5.3c2,0,2.1-1.6,2.1-2.6v-8l-13.9-16.1c-1.3-1.5-2.2-1.6-4-1.6h-2.1v-5.9h26.4v5.9h-5.2c-.9,0-1.2.5-1.2,1.1,0,.9,1.9,1.9,8.8,10.6,7.6-8.5,9-9.5,9-10.6s-.9-1.1-2.1-1.1h-4v-5.9h21.6v5.9h-2c-1.9,0-3,0-4.8,2.1l-13.2,14.8v9.5c0,1.9,1.4,1.9,2.9,1.9h4.5v6h-28.1v-6Z"
              />
              <path
                fill="#fff"
                d="M183.3,727c1,0,1.9-.6,1.9-1.9v-24.3c0-1.5-1-2.2-2.2-2.2h-5.1v-5.9h49.3v16h-6.4c-.2-4.1-.2-10-6.7-10h-15.5v10.4h5.5c2.1,0,3.6-1.1,3.6-3.2v-2.9h6.2v18.2h-6.2v-2.9c0-2.6-1.2-3.2-3.5-3.2h-5.5v11.8h16c7.2,0,6.5-8.5,6.7-11.5h6.5v17.5h-50v-6h5.4Z"
              />
              <path
                fill="#fff"
                d="M234.8,727c1.9,0,2.9-1.2,3.3-2.1l15.6-32.6h11.5l15.6,32.1c.9,2,1.6,2.6,5,2.6h1.9v6h-26.3v-6h4.9c1.2,0,1.7-.4,1.7-1.2,0-1.3-1.3-3.5-2.6-5.9h-17.6c-1.4,3-2.5,5-2.5,5.8s.9,1.3,3.3,1.3h3.4v6h-20.7v-6h3.3ZM256.7,701.6l-6,12.4h12.1l-6.1-12.4Z"
              />
              <path
                fill="#fff"
                d="M310.9,725.7c0,.7.9,1.3,2,1.3h5.2v5.9h-27.9v-5.9h5.4c1,0,1.9-.6,1.9-1.3v-25.7c0-.7-.9-1.3-1.9-1.3h-5.4v-6h33.5c8.3,0,16.4,1.8,16.4,10s-6.4,9.3-13.9,9.8c5.7,1,9.1,2.1,9.8,7.5.3,2.4.2,7,2.2,7s1.7-.9,1.7-2.7v-4.1h6.1v3.7c0,8.3-3.7,10-12.3,10s-9.3-2-10-7.1l-1.1-7.5c-.2-1.5-1-3.7-4.9-3.7h-6.9v10.2ZM310.9,709.4h7.1c4.7,0,8.1-.6,8.1-5.7s-2.6-5.1-6.7-5.1h-8.4v10.7Z"
              />
              <path
                fill="#fff"
                d="M390.5,707.1h-6.4c-1.8-6.3-7.5-9.6-14.4-9.6s-8.6,1.1-8.6,4.6,1.2,2.6,3.2,3l16.2,3.4c7.3,1.5,12.5,4.9,12.5,11.9s-9.3,13.7-19.1,13.7-10.8-.9-15.5-3.5l-1.8,3h-6.1v-15.6h6.5c1.4,6.8,7.9,10,15.8,10s9.6-1,9.6-4.8-1.9-2.9-4.3-3.3l-17.6-3.7c-6.6-1.4-10.4-5.6-10.4-11.4,0-9,9.2-13.4,18.8-13.4s10.4.9,13.6,3.3l2-2.7h6.1v15.1Z"
              />
              <path
                fill="#fff"
                d="M441.9,691.4c15.2,0,26.4,8.2,26.4,21.4s-10.8,21.2-26.7,21.2-26.7-8.1-26.7-21.2,10.9-21.4,27-21.4M441.5,727.9c10.6,0,11.8-8.2,11.8-14.9s-.8-15.4-11.7-15.4-12,8.7-12,15.2,1.5,15.1,11.8,15.1"
              />
              <path
                fill="#fff"
                d="M501.1,727v6h-28.2v-6h5.4c1,0,1.9-.6,1.9-1.9v-24.3c0-1.5-1-2.2-2.1-2.2h-5.1v-5.9h48.9v16h-6.4c-.6-4.1-.2-10-6-10h-15.9v10.4h5.1c2.2,0,3.6-1.7,3.6-3.7v-2.4h6.3v18.2h-6.3v-2c0-2.6-1.2-4.1-3.5-4.1h-5.2v10c0,1.3.9,1.9,2,1.9h5.5Z"
              />
              <path
                fill="#fff"
                d="M548.6,727c1,0,2-.7,2-2v-24.3c0-1.3-.7-1.9-2.2-1.9h-5.3v-5.9h32.5c14.1,0,23.8,8.8,23.8,20.2s-9.6,20-24.2,20h-32.1v-6h5.4ZM564,727h9.3c10.3,0,12.3-8.5,12.3-14.2s-2.6-14.1-11.9-14.1h-9.7v28.3Z"
              />
              <path
                fill="#fff"
                d="M604.1,727c1.9,0,2.9-1.2,3.3-2.1l15.6-32.6h11.5l15.6,32.1c.9,2,1.6,2.6,5,2.6h1.9v6h-26.3v-6h4.9c1.2,0,1.7-.4,1.7-1.2,0-1.3-1.3-3.5-2.6-5.9h-17.6c-1.4,3-2.5,5-2.5,5.8s.9,1.3,3.3,1.3h3.4v6h-20.7v-6h3.3ZM625.9,701.6l-6,12.4h12.1l-6.1-12.4Z"
              />
              <path
                fill="#fff"
                d="M680.2,725.1c0,1.3.9,1.9,1.9,1.9h5.2v5.9h-27.8v-5.9h5.4c1,0,1.9-.6,1.9-1.9v-24.6c0-1.3-.9-1.9-1.9-1.9h-5.4v-6h31.7c8.9,0,17.3,3.4,17.3,11.7s-6,11.7-14,11.7h-14.4v8.9ZM680.2,710.3h8.2c4.6,0,6.6-2.6,6.6-6s-2-5.7-6.5-5.7h-8.3v11.6Z"
              />
              <path
                fill="#fff"
                d="M733.4,725.1c0,1.3.9,1.9,1.9,1.9h5.2v5.9h-27.8v-5.9h5.4c1,0,1.9-.6,1.9-1.9v-24.6c0-1.3-.9-1.9-1.9-1.9h-5.4v-6h31.7c8.9,0,17.3,3.4,17.3,11.7s-6,11.7-14,11.7h-14.4v8.9ZM733.4,710.3h8.2c4.6,0,6.6-2.6,6.6-6s-2-5.7-6.5-5.7h-8.3v11.6Z"
              />
              <path
                fill="#fff"
                d="M771.4,727c1,0,1.9-.6,1.9-1.9v-24.3c0-1.5-1-2.2-2.2-2.2h-5.1v-5.9h49.3v16h-6.4c-.2-4.1-.2-10-6.6-10h-15.5v10.4h5.5c2.1,0,3.6-1.1,3.6-3.2v-2.9h6.2v18.2h-6.2v-2.9c0-2.6-1.2-3.2-3.5-3.2h-5.5v11.8h16c7.2,0,6.5-8.5,6.7-11.5h6.5v17.5h-50v-6h5.4Z"
              />
              <path
                fill="#fff"
                d="M843.2,725.7c0,.7.9,1.3,2,1.3h5.3v5.9h-27.9v-5.9h5.4c1,0,1.9-.6,1.9-1.3v-25.7c0-.7-.9-1.3-1.9-1.3h-5.4v-6h33.5c8.3,0,16.4,1.8,16.4,10s-6.4,9.3-13.9,9.8c5.7,1,9.1,2.1,9.8,7.5.3,2.4.2,7,2.2,7s1.7-.9,1.7-2.7v-4.1h6.1v3.7c0,8.3-3.7,10-12.3,10s-9.3-2-10-7.1l-1.1-7.5c-.2-1.5-1-3.7-4.9-3.7h-6.9v10.2ZM843.2,709.4h7.1c4.7,0,8.1-.6,8.1-5.7s-2.6-5.1-6.7-5.1h-8.4v10.7Z"
              />
            </svg>
          </div>
        </div>
      </section>

      {/* Countdown + CTA + Video Section */}
      <section ref={ctaSectionRef} className="relative bg-stone-950 py-24 md:py-32">
        <div className="container flex flex-col items-center text-center py-8 md:py-20">
          <p data-reveal className="text-dgr-gold text-lg uppercase font-bold tracking-widest">
            {t('eventDate')}
          </p>
          <p data-reveal className="mt-4 max-w-lg text-lg text-stone-400">
            {t('subtitle')}
          </p>

          {/* Countdown */}
          <div data-reveal className="mt-10 flex gap-4 sm:gap-6">
            {[
              { value: countdown.days, label: t('countdownDays') },
              { value: countdown.hours, label: t('countdownHours') },
              { value: countdown.minutes, label: t('countdownMinutes') },
              { value: countdown.seconds, label: t('countdownSeconds') },
            ].map((unit) => (
              <div key={unit.label} className="flex flex-col items-center">
                <div className="flex h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 items-center justify-center rounded-xl border border-dgr-gold/30 bg-stone-900/80 backdrop-blur-sm">
                  <span className="text-2xl sm:text-3xl md:text-4xl font-bold tabular-nums text-dgr-gold">
                    {String(unit.value).padStart(2, '0')}
                  </span>
                </div>
                <span className="mt-2 text-[10px] sm:text-xs uppercase tracking-wider text-dgr-gold/60">
                  {unit.label}
                </span>
              </div>
            ))}
          </div>

          {/* Register CTA */}
          <div data-reveal>
            <Button
              size="lg"
              asChild
              className="mt-10 text-black text-base px-10 bg-dgr-gold hover:bg-dgr-gold/90"
            >
              <Link href={registerHref} prefetch={false}>
                {tNav('register')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Video */}
          <div data-reveal ref={videoRef} className="mt-16" style={{ width: '65%' }}>
            <div className="overflow-hidden rounded-2xl bg-stone-900 shadow-2xl shadow-black/40">
              <YouTubeEmbed videoId={YOUTUBE_ID} />
            </div>
          </div>
        </div>
      </section>

      {/* DGR Ride Day 2026 Section */}
      <section className="relative overflow-hidden bg-stone-950 py-20 md:py-28">
        {/* Subtle radial highlights */}
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-dgr-gold/10 blur-3xl" />
          <div className="absolute -bottom-40 right-0 h-[28rem] w-[28rem] rounded-full bg-dgr-gold/5 blur-3xl" />
        </div>

        <div className="container relative">
          {/* Heading */}
          <div className="mx-auto max-w-3xl text-center">
            <p
              data-rideday-reveal
              className="text-xs uppercase tracking-[0.3em] font-medium text-dgr-gold"
            >
              May 17, 2026
            </p>
            <h2
              data-rideday-reveal
              className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white"
            >
              DGR Ride Day <span className="text-dgr-gold">2026</span>
            </h2>
            <p
              data-rideday-reveal
              className="mt-5 text-base md:text-lg leading-relaxed text-stone-400"
            >
              One day. One ride. Twenty-four hours of distinguished gentlemen circling the globe —
              together.
            </p>
          </div>

          {/* CTA Cards */}
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                href: DGR_INSTAGRAM_URL,
                external: true,
                bg: '/images/dgr/HeroImage.jpg',
                icon: <Instagram className="h-5 w-5" strokeWidth={1.8} />,
                eyebrow: 'Live on Instagram',
                title: 'Follow the ride live',
                desc: 'Watch riders roll out across the world in real time.',
                cta: 'Watch on Instagram',
              },
              {
                href: buildLocalePath(locale, '/upload'),
                external: false,
                bg: '/images/dgr/RiderProfile.jpg',
                icon: <Camera className="h-5 w-5" strokeWidth={1.8} />,
                eyebrow: 'After the ride',
                title: 'Upload your photos',
                desc: 'Add your photos to the global gallery and share your ride.',
                cta: 'Upload photos',
              },
              {
                href: buildLocalePath(locale, '/gallery'),
                external: false,
                bg: '/images/dgr/DGR2026_Live.jpg',
                icon: <Images className="h-5 w-5" strokeWidth={1.8} />,
                eyebrow: 'Live gallery',
                title: 'See the world ride',
                desc: 'Photos pour in live from every city. Watch the gallery fill in real time.',
                cta: 'Open gallery',
              },
            ].map((card) => {
              const inner = (
                <div className="relative h-[26rem] overflow-hidden rounded-2xl border border-white/10 bg-stone-900 shadow-2xl shadow-black/40 transition-transform duration-500 ease-out group-hover:-translate-y-2 group-hover:border-dgr-gold/40 group-hover:shadow-dgr-gold/10">
                  {/* Background image */}
                  <div className="absolute inset-0">
                    <Image
                      src={card.bg}
                      alt=""
                      fill
                      sizes="(min-width: 768px) 33vw, 100vw"
                      className="object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    />
                  </div>
                  {/* Gradient overlays */}
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/70 to-stone-950/10 transition-opacity duration-500 group-hover:opacity-90" />
                  <div className="absolute inset-0 bg-stone-950/30 transition-opacity duration-500 group-hover:bg-stone-950/10" />
                  {/* Top-right glyph */}
                  <div className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-md transition-all duration-500 group-hover:border-dgr-gold group-hover:bg-dgr-gold group-hover:text-stone-950">
                    {card.icon}
                  </div>
                  {/* Bottom content */}
                  <div className="absolute inset-x-0 bottom-0 p-7 md:p-8">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-dgr-gold">
                      {card.eyebrow}
                    </p>
                    <h3 className="mt-3 text-2xl md:text-[28px] font-bold leading-tight text-white">
                      {card.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-stone-300/90">{card.desc}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white transition-colors group-hover:text-dgr-gold">
                      <span>{card.cta}</span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                  {/* Gold underline reveal */}
                  <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-dgr-gold transition-all duration-500 group-hover:w-full" />
                </div>
              );
              return card.external ? (
                <a
                  key={card.title}
                  data-rideday-reveal
                  href={card.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  {inner}
                </a>
              ) : (
                <Link
                  key={card.title}
                  data-rideday-reveal
                  href={card.href}
                  prefetch={false}
                  className="group block"
                >
                  {inner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <main className="flex-1">
        {/* Globe Map Section */}
        <section className="relative bg-[#faf8f2] py-16 md:py-24">
          <div className="container">
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-dgr-gold mb-3">
                {tEvents('findYourRide')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900">
                {tEvents('eventsWorldwide')}
              </h2>
            </div>

            {/* Country selector */}
            <div className="flex flex-col items-center gap-3 mb-8">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                  onBlur={() => setTimeout(() => setCountryDropdownOpen(false), 200)}
                  className="flex items-center justify-between w-72 md:w-80 h-11 px-4 bg-white border border-stone-200 rounded-xl text-sm text-stone-900 hover:border-dgr-gold transition-colors"
                >
                  <span className={selectedCountry ? 'text-stone-900' : 'text-stone-400'}>
                    {selectedCountry
                      ? (sortedCountries.find((c) => c.countryCode === selectedCountry)
                          ?.countryName ?? tEvents('selectCountry'))
                      : tEvents('selectCountry')}
                  </span>
                  <ChevronRight
                    className={`w-4 h-4 text-stone-400 transition-transform ${
                      countryDropdownOpen ? 'rotate-90' : ''
                    }`}
                  />
                </button>

                {countryDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-72 md:w-80 bg-white rounded-xl shadow-lg border border-stone-200 max-h-64 overflow-y-auto z-20">
                    {sortedCountries.map((country) => (
                      <button
                        key={country.countryCode}
                        onClick={() => {
                          setSelectedCountry(country.countryCode);
                          setCountryDropdownOpen(false);
                          if (!globeMap.current) return;
                          const countryRides = globalRides.filter(
                            (r) => r.countryCode === country.countryCode,
                          );
                          if (!countryRides.length) return;
                          const allCoords = countryRides
                            .map((r) => parseCoordinates(r.coordinates))
                            .filter(Boolean) as { lat: number; lng: number }[];
                          if (!allCoords.length) return;
                          if (allCoords.length === 1) {
                            globeMap.current.flyTo({
                              center: [allCoords[0].lng, allCoords[0].lat],
                              zoom: 6,
                              duration: 2000,
                            });
                          } else {
                            const bounds = new window.mapboxgl.LngLatBounds();
                            allCoords.forEach((c) => bounds.extend([c.lng, c.lat]));
                            globeMap.current.fitBounds(bounds.toArray(), {
                              padding: 80,
                              maxZoom: 10,
                            });
                          }
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50 transition-colors text-left"
                      >
                        <Flag code={country.countryCode} size="sm" className="rounded" />
                        <span className="flex-1 text-sm font-medium text-stone-900">
                          {country.countryName}
                        </span>
                        <span className="text-xs text-stone-400">
                          {country.ridesCount} {tEvents('rides').toLowerCase()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedCountry && (
                <Link
                  href={`/events/${
                    sortedCountries.find((c) => c.countryCode === selectedCountry)?.slug ?? ''
                  }`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-dgr-gold text-black font-medium rounded-xl hover:bg-dgr-gold/90 transition-colors text-sm"
                >
                  {tEvents('viewAllEventsIn', {
                    country:
                      sortedCountries.find((c) => c.countryCode === selectedCountry)?.countryName ??
                      '',
                  })}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>

            <div className="relative mx-auto max-w-5xl">
              {/* Search bar */}
              <div className="absolute top-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-[420px] z-10">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    placeholder={tEvents('searchCities')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                    className="h-11 pl-12 pr-12 bg-white border-stone-200 shadow-lg text-stone-900 placeholder:text-stone-400 text-sm rounded-xl"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}

                  {searchFocused && filteredRides.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden">
                      {filteredRides.map((ride) => (
                        <button
                          key={ride.id}
                          onClick={() => flyToRide(ride)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors text-left"
                        >
                          <Flag code={ride.countryCode} size="sm" className="rounded" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-900 text-sm">{ride.city}</p>
                            <p className="text-xs text-stone-500 truncate">
                              {ride.stateName ? `${ride.stateName}, ` : ''}
                              {ride.countryName}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {searchFocused && searchQuery && filteredRides.length === 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-stone-200 p-4 text-center">
                      <p className="text-stone-500 text-sm">{tEvents('noCitiesFound')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Globe map */}
              <div
                ref={mapContainer}
                className="h-[600px] md:h-[800px] w-full rounded-2xl overflow-hidden bg-[#faf8f2]"
              />

              {!globeMapInitialized && MAPBOX_TOKEN && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#faf8f2] rounded-2xl z-[5]">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">{tEvents('loadingGlobalMap')}</p>
                  </div>
                </div>
              )}

              {!MAPBOX_TOKEN && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#faf8f2] rounded-2xl z-[5]">
                  <div className="text-center">
                    <MapPin className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">{tEvents('mapNotAvailable')}</p>
                  </div>
                </div>
              )}

              {/* Status legend */}
              <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-lg shadow-md px-3 py-2.5 z-10">
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-orange-600 border border-white shadow-sm" />
                    <span className="text-stone-500">{tEvents('statusAlmostFull')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-600 border border-white shadow-sm" />
                    <span className="text-stone-500">{tEvents('statusFull')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DGR 2026 Animation Section */}
        <div ref={dgrSectionRef} className="relative overflow-x-clip">
          {/* Top half: white bg for DGR letters + stats */}
          <div className="relative bg-white pb-0">
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-dgr-gold" />
            <div className="container relative" style={{ minHeight: '450px', maxHeight: '500px' }}>
              {/* Stats - mobile only (desktop stats are inside the DGR section) */}
              <div ref={statsMobileRef} className="md:hidden py-6">
                <div className="container px-0">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      {
                        value: '$60.5M',
                        label: t('fundsRaised'),
                        icon: '/images/dgr/icons/FundsRaised.svg',
                      },
                      {
                        value: '130K',
                        label: t('ridersParticipated'),
                        icon: '/images/dgr/icons/TopRiders.svg',
                      },
                      {
                        value: '110',
                        label: t('countriesRepresented'),
                        icon: '/images/dgr/icons/TopCountries.svg',
                      },
                      {
                        value: '1070+',
                        label: t('citiesWorldwide'),
                        icon: '/images/dgr/icons/TopCities.svg',
                      },
                    ].map((stat) => (
                      <div key={stat.label} data-stat className="flex items-center gap-2.5">
                        <img src={stat.icon} alt="" className="w-8 h-8 flex-shrink-0" />
                        <div>
                          <div className="text-3xl font-bold tracking-tight text-stone-900">
                            {stat.value}
                          </div>
                          <p className="text-sm text-stone-500">{stat.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Stats in the right white space (desktop only) */}
              <div
                ref={statsDesktopRef}
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 w-[60%] items-center justify-center"
              >
                <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                  <div
                    data-stat
                    className="hidden md:block text-4xl font-bold tracking-tight text-stone-900 col-span-2 text-center mb-6"
                  >
                    {t('ourImpact')}
                  </div>

                  {[
                    {
                      value: '$60.5M',
                      label: t('fundsRaised'),
                      icon: '/images/dgr/icons/FundsRaised.svg',
                    },
                    {
                      value: '130K',
                      label: t('ridersParticipated'),
                      icon: '/images/dgr/icons/TopRiders.svg',
                    },
                    {
                      value: '110',
                      label: t('countriesRepresented'),
                      icon: '/images/dgr/icons/TopCountries.svg',
                    },
                    {
                      value: '1070+',
                      label: t('citiesWorldwide'),
                      icon: '/images/dgr/icons/TopCities.svg',
                    },
                  ].map((stat) => (
                    <div key={stat.label} data-stat className="flex items-center gap-3">
                      <img src={stat.icon} alt="" className="w-14 h-14 flex-shrink-0" />
                      <div>
                        <div className="text-4xl font-bold tracking-tight text-stone-900">
                          {stat.value}
                        </div>
                        <p className="text-md text-stone-500">{stat.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 502 450"
                className="absolute left-0 bottom-0 w-[60%] max-w-[350px] md:w-[40%] md:max-w-[320px]"
                style={{ overflow: 'visible' }}
                aria-label="DGR Letters"
              >
                {/* Gold bleed rect: extends left beyond SVG bounds to screen edge */}
                <rect x="-2000" y="426" width="4502" height="24" fill="hsl(var(--dgr-gold))" />
                <g ref={dgrDRef}>
                  <path
                    d="M165.57 213.02H135.3C212.28 213.02 259.72 173.76 259.72 107.28C259.73 46.69 211.98 0 137.16 0H0V31.54H17.99C25.72 31.54 29.43 34.94 29.43 41.74V170.67C29.43 177.78 24.18 181.49 18.92 181.49H0V426.83H250.96V214.8L208.21 236.08L165.57 213.02ZM100.23 31.54H126.65C175.81 31.54 185.84 72.97 185.84 106.36C185.84 136.66 179.21 181.49 124.49 181.49H100.24V31.54H100.23Z"
                    fill="black"
                  />
                  <path
                    d="M65.9104 322.26C65.9104 358.74 79.1204 398.93 134.44 398.93C160.87 398.93 179.31 386.56 179.31 373.27V355.79C179.31 345.9 175.62 340.95 166.71 340.95H128.19V302.46H250.96V282.67H197.95C189.65 256.55 176.23 241.25 136.89 241.25C78.5004 241.25 65.9004 282.37 65.9004 322.26H65.9104Z"
                    fill="black"
                  />
                  <path
                    d="M171.01 426.83H205.13L205.11 407.36C197.06 416.18 184.85 422.55 171.01 426.83Z"
                    fill="black"
                  />
                  <path d="M0 365.94V426.83H72.07C37.9 416.3 12.63 394.57 0 365.94Z" fill="black" />
                  <path
                    d="M126.76 207.24C137.48 207.24 149.33 208.82 160.83 211.45C222.63 203.55 259.73 166.05 259.73 107.28C259.73 48.51 211.98 0 137.16 0H0V31.54H17.99C25.72 31.54 29.43 34.94 29.43 41.74V170.67C29.43 177.78 24.18 181.49 18.92 181.49H0V277.79C19.07 234.73 66.61 207.24 126.76 207.24ZM100.23 31.54H126.65C175.81 31.54 185.84 72.97 185.84 106.36C185.84 136.66 179.21 181.49 124.49 181.49H100.24V31.54H100.23Z"
                    fill="black"
                  />
                </g>
                <g ref={dgrGRef}>
                  <path
                    d="M250.96 426.83V396.26H273.75C279 396.26 283.64 393.17 283.64 389.46V253.11C283.64 249.4 279 246.31 273.75 246.31H250.96V213.03H205.12V228.52C187.3 217.08 153.49 207.25 126.75 207.25C66.6002 207.25 19.0602 234.74 -0.00976562 277.8V365.95C12.6202 394.58 37.8902 416.31 72.0602 426.84H0.000234375V426.84H501.93V426.84H250.97L250.96 426.83ZM136.9 241.25C176.24 241.25 189.66 256.55 197.96 282.67H250.97V302.46H128.2V340.95H166.72C175.63 340.95 179.32 345.9 179.32 355.79V373.27C179.32 386.56 160.88 398.93 134.45 398.93C79.1302 398.93 65.9202 358.74 65.9202 322.26C65.9202 282.37 78.5202 241.25 136.91 241.25H136.9ZM171.01 426.83C184.85 422.55 197.06 416.18 205.11 407.36L205.13 426.83H171.01Z"
                    fill="hsl(var(--dgr-gold))"
                  />
                </g>
                <g ref={dgrRRef}>
                  <path
                    d="M389.18 303.19C414.22 303.19 429.6 300.1 429.6 273.2C429.6 255.27 418.55 246.3 396.6 246.3H357.27V303.19H389.18Z"
                    fill="hsl(var(--dgr-gold))"
                  />
                  <path
                    d="M416.21 213.02C460.11 214.76 494.27 224.35 494.27 267.94C494.27 306.59 469.08 318.08 429.5 320.55C463.97 322.25 490.95 329.96 490.95 359.15C490.95 390.23 494.23 396.25 501.93 396.25V213.02H416.21Z"
                    fill="hsl(var(--dgr-gold))"
                  />
                  <path
                    d="M498.41 426.83L469.84 426.77C427.9 426.77 419.12 418.09 419.12 390.8C419.12 367.85 418.62 361.45 418.62 361.45C417.38 353.41 413.36 341.66 392.65 341.66H357.27V389.45C357.27 393.16 362.21 396.25 367.78 396.25H393.53V426.82H498.41V426.83Z"
                    fill="hsl(var(--dgr-gold))"
                  />
                </g>
              </svg>
            </div>
          </div>

          {/* Bottom half: gold bg for 15 badge + CTA */}
          <div className="bg-dgr-gold py-10">
            <div className="container relative flex flex-col items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 267 215"
                className="w-[30%] max-w-[220px]"
                aria-label="15 Years of Dapper"
              >
                <g ref={dgr15Ref} transform="translate(-18, -467)">
                  <path
                    d="M32.1797 561.18C41.1097 578.69 52.0697 570.6 52.0697 570.6C52.0697 570.6 51.3897 557 32.1797 561.18Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M42.1596 557.35C42.1596 557.35 44.9896 544.03 25.3496 543.15C29.4896 562.36 42.1596 557.35 42.1596 557.35Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M36.5804 541.35C36.5804 541.35 42.5804 529.12 23.7504 523.47C23.0704 543.11 36.5804 541.35 36.5804 541.35Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M34.92 524.39C34.92 524.39 43.98 514.22 27.36 503.72C21.43 522.46 34.92 524.39 34.92 524.39Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M37.8197 507.03C37.8197 507.03 48.7397 498.89 34.5797 485.26C24.9997 502.42 37.8197 507.03 37.8197 507.03Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M55.2595 475.58C37.1095 483.13 44.3295 494.68 44.3295 494.68C44.3295 494.68 57.9495 495.05 55.2595 475.58Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M52.2402 530.5C35.3702 540.59 44.1802 550.98 44.1802 550.98C44.1802 550.98 57.7102 549.38 52.2402 530.5Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M49.96 513.55C32.14 521.84 39.83 533.09 39.83 533.09C39.83 533.09 53.45 532.9 49.96 513.55Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M55.2599 494.59C36.4499 500.29 42.4799 512.51 42.4799 512.51C42.4799 512.51 55.9899 514.23 55.2599 494.59Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M214.76 570.6C214.76 570.6 225.72 578.69 234.65 561.18C215.44 557 214.76 570.6 214.76 570.6Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M224.66 557.35C224.66 557.35 237.33 562.36 241.47 543.15C221.83 544.03 224.66 557.35 224.66 557.35Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M230.24 541.35C230.24 541.35 243.75 543.11 243.07 523.47C224.24 529.12 230.24 541.35 230.24 541.35Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M231.9 524.39C231.9 524.39 245.38 522.46 239.46 503.72C222.84 514.22 231.9 524.39 231.9 524.39Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M229 507.03C229 507.03 241.82 502.43 232.24 485.26C218.08 498.89 229 507.03 229 507.03Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M222.49 494.69C222.49 494.69 229.71 483.14 211.56 475.59C208.87 495.06 222.49 494.69 222.49 494.69Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M212.3 547.78C212.75 545.16 212.98 542.42 212.98 539.57C212.98 515.28 196.49 500.2 169.95 500.2C163.58 500.2 157.96 500.61 152.14 502.39V492.74H201.39L201.81 467.58H121.94L121.82 531.44H147.56L148.19 529.65C149.52 525.82 154.95 521.72 160.91 521.72C176.28 521.72 177.94 535.4 177.94 541.28C177.94 551.22 171.83 561.92 158.42 561.92C155.61 561.92 152.96 561.7 150.66 561.28C153.17 558.65 154.44 555.68 154.44 552.4C154.44 543.57 147.09 536.91 137.34 536.91C126.63 536.91 118.85 544.53 118.85 555.02C118.85 556.47 118.98 557.9 119.25 559.29H114.88C112.32 559.29 111.22 558.33 111.22 556.1V467.58H87.4598L86.8698 469.44C81.6598 485.77 76.1198 486.31 63.9798 487.51L61.5898 487.75L61.4098 508.32H76.2498V556.25C76.2498 558.99 74.0198 559.29 73.0598 559.29H59.5098C60.8198 556.12 61.0398 551.44 58.3898 544.56C42.4498 556.06 52.1198 565.65 52.1198 565.65C52.1198 565.65 52.3298 565.61 52.6798 565.5V580.38H135.29V577.4C145.2 581.86 156.34 582.39 161.08 582.39C175.93 582.39 188.56 578.85 197.59 572.16C201.92 568.95 205.37 565.03 207.9 560.51C210.31 564.74 214.7 565.66 214.7 565.66C214.7 565.66 222.61 557.8 212.31 547.79L212.3 547.78ZM161.08 577.04C150.77 577.04 140.64 574.67 133.99 570.71L129.95 568.3V575.03H58.0298V564.63H73.0698C77.3098 564.63 81.6098 561.75 81.6098 556.24V502.96H66.8198L66.9098 492.58C77.9498 491.42 85.5598 489.61 91.3598 472.92H105.89V556.09C105.89 561.28 109.42 564.63 114.89 564.63H126.63L125.28 561.02C124.56 559.08 124.21 557.12 124.21 555.01C124.21 547.38 129.49 542.25 137.35 542.25C143.05 542.25 149.1 545.8 149.1 552.39C149.1 555.26 147.29 557.81 143.55 560.16L139.24 562.88L143.92 564.88C148.75 566.95 155.08 567.26 158.43 567.26C174.75 567.26 183.29 554.19 183.29 541.27C183.29 525.91 174.71 516.36 160.91 516.36C153.93 516.36 146.87 520.51 143.92 526.08H127.18L127.28 472.91H196.38L196.14 487.37H146.8V510.12L150.48 508.64C156.89 506.06 162.68 505.54 169.96 505.54C193.56 505.54 207.65 518.26 207.65 539.57C207.65 563.03 190.24 577.04 161.09 577.04H161.08Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M223.14 550.98C223.14 550.98 231.95 540.59 215.08 530.5C209.61 549.38 223.14 550.98 223.14 550.98Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M216.87 513.55C213.38 532.89 227 533.09 227 533.09C227 533.09 234.69 521.85 216.87 513.55Z"
                    fill="#FFF9D9"
                  />
                  <path
                    d="M224.34 512.5C224.34 512.5 230.37 500.29 211.56 494.58C210.83 514.22 224.34 512.5 224.34 512.5Z"
                    fill="#FFF9D9"
                  />
                </g>
                <g ref={dgrDapperRef} transform="translate(-18, -467)">
                  <path
                    d="M31.4602 596.22H32.9202C33.4702 596.22 33.5102 595.78 33.5102 595.49V593.26L29.6402 588.77C29.2802 588.37 29.0202 588.33 28.5402 588.33H27.9502V586.67H35.3202V588.33H33.8702C33.6302 588.33 33.5502 588.48 33.5502 588.64C33.5502 588.9 34.0702 589.18 36.0002 591.6C38.1302 589.24 38.5002 588.96 38.5002 588.64C38.5002 588.36 38.2402 588.33 37.9102 588.33H36.8102V586.67H42.8302V588.33H42.2602C41.7202 588.33 41.4102 588.34 40.9102 588.91L37.2302 593.04V595.68C37.2302 596.2 37.6202 596.22 38.0302 596.22H39.2802V597.88H31.4602V596.22Z"
                    fill="black"
                  />
                  <path
                    d="M45.0796 596.22C45.3596 596.22 45.5996 596.06 45.5996 595.7V588.93C45.5996 588.51 45.3196 588.33 44.9996 588.33H43.5696V586.67H57.2996V591.12H55.5096C55.4596 589.97 55.4496 588.32 53.6596 588.32H49.3496V591.22H50.8796C51.4596 591.22 51.8696 590.91 51.8696 590.34V589.53H53.6096V594.61H51.8696V593.81C51.8696 593.1 51.5396 592.92 50.8896 592.92H49.3396V596.22H53.8096C55.8296 596.22 55.6296 593.86 55.6796 593.02H57.4996V597.88H43.5596V596.22H45.0696H45.0796Z"
                    fill="black"
                  />
                  <path
                    d="M59.4503 596.22C59.9903 596.22 60.2503 595.88 60.3803 595.62L64.7403 586.54H67.9403L72.2903 595.48C72.5503 596.03 72.7403 596.21 73.6903 596.21H74.2103V597.87H66.8703V596.21H68.2403C68.5803 596.21 68.7103 596.1 68.7103 595.88C68.7103 595.53 68.3403 594.91 68.0003 594.22H63.1003C62.7103 595.05 62.4003 595.62 62.4003 595.85C62.4003 596.13 62.6603 596.21 63.3303 596.21H64.2903V597.87H58.5303V596.21H59.4603L59.4503 596.22ZM65.5403 589.16L63.8603 592.61H67.2303L65.5403 589.16Z"
                    fill="black"
                  />
                  <path
                    d="M80.6702 595.86C80.6702 596.06 80.9302 596.22 81.2202 596.22H82.6802V597.88H74.9102V596.22H76.4202C76.7002 596.22 76.9402 596.06 76.9402 595.86V588.69C76.9402 588.49 76.7002 588.34 76.4202 588.34H74.9102V586.68H84.2502C86.5602 586.68 88.8202 587.18 88.8202 589.48C88.8202 591.51 87.0301 592.07 84.9501 592.2C86.5301 592.47 87.4902 592.78 87.6802 594.28C87.7602 594.96 87.7302 596.23 88.3002 596.23C88.6702 596.23 88.7902 595.97 88.7902 595.47V594.32H90.5002V595.36C90.5002 597.68 89.4702 598.14 87.0802 598.14C85.6302 598.14 84.4902 597.59 84.2802 596.17L83.9702 594.07C83.9102 593.65 83.6902 593.03 82.6002 593.03H80.6702V595.88V595.86ZM80.6702 591.33H82.6402C83.9502 591.33 84.9002 591.17 84.9002 589.75C84.9002 588.8 84.1902 588.34 83.0302 588.34H80.6802V591.33H80.6702Z"
                    fill="black"
                  />
                  <path
                    d="M102.84 590.68H101.07C100.57 588.92 98.9698 588.01 97.0498 588.01C95.9998 588.01 94.6598 588.32 94.6598 589.3C94.6598 589.75 94.9998 590.03 95.5598 590.15L100.06 591.09C102.09 591.51 103.54 592.46 103.54 594.41C103.54 596.93 100.94 598.23 98.1998 598.23C96.6098 598.23 95.1798 597.98 93.8898 597.25L93.3898 598.1H91.6998V593.74H93.4998C93.8898 595.63 95.7098 596.52 97.8898 596.52C98.9498 596.52 100.56 596.23 100.56 595.17C100.56 594.62 100.02 594.37 99.3698 594.24L94.4598 593.22C92.6198 592.83 91.5498 591.66 91.5498 590.05C91.5498 587.55 94.1198 586.31 96.7898 586.31C98.3198 586.31 99.6898 586.57 100.58 587.24L101.15 586.48H102.84V590.69V590.68Z"
                    fill="black"
                  />
                  <path
                    d="M117.17 586.3C121.42 586.3 124.54 588.58 124.54 592.27C124.54 595.96 121.52 598.19 117.11 598.19C112.93 598.19 109.66 595.93 109.66 592.27C109.66 588.61 112.7 586.3 117.17 586.3ZM117.06 596.48C120.01 596.48 120.36 594.19 120.36 592.32C120.36 590.45 120.13 588.03 117.11 588.03C114.09 588.03 113.77 590.47 113.77 592.28C113.77 594.09 114.18 596.49 117.06 596.49V596.48Z"
                    fill="black"
                  />
                  <path
                    d="M133.66 596.22V597.88H125.81V596.22H127.32C127.6 596.22 127.84 596.06 127.84 595.7V588.93C127.84 588.53 127.56 588.33 127.24 588.33H125.81V586.67H139.44V591.12H137.67C137.49 589.97 137.61 588.32 136.01 588.32H131.59V591.22H133.02C133.62 591.22 134.01 590.75 134.01 590.18V589.53H135.77V594.61H134.01V594.06C134.01 593.33 133.67 592.92 133.04 592.92H131.59V595.7C131.59 596.05 131.85 596.22 132.14 596.22H133.67H133.66Z"
                    fill="black"
                  />
                  <path
                    d="M146.91 596.22C147.19 596.22 147.46 596.02 147.46 595.65V588.87C147.46 588.51 147.26 588.33 146.86 588.33H145.4V586.67H154.44C158.38 586.67 161.08 589.12 161.08 592.31C161.08 595.81 158.39 597.87 154.34 597.87H145.4V596.21H146.91V596.22ZM151.19 596.22H153.78C156.66 596.22 157.2 593.86 157.2 592.27C157.2 590.51 156.48 588.33 153.9 588.33H151.2V596.22H151.19Z"
                    fill="black"
                  />
                  <path
                    d="M162.37 596.22C162.91 596.22 163.17 595.88 163.3 595.62L167.66 586.54H170.86L175.21 595.48C175.47 596.03 175.66 596.21 176.61 596.21H177.13V597.87H169.79V596.21H171.16C171.5 596.21 171.63 596.1 171.63 595.88C171.63 595.53 171.26 594.91 170.92 594.22H166.02C165.63 595.05 165.32 595.62 165.32 595.85C165.32 596.13 165.58 596.21 166.25 596.21H167.21V597.87H161.45V596.21H162.38L162.37 596.22ZM168.45 589.16L166.77 592.61H170.14L168.45 589.16Z"
                    fill="black"
                  />
                  <path
                    d="M183.59 595.7C183.59 596.05 183.83 596.22 184.13 596.22H185.59V597.88H177.83V596.22H179.34C179.62 596.22 179.86 596.06 179.86 595.7V588.85C179.86 588.49 179.62 588.33 179.34 588.33H177.83V586.67H186.67C189.16 586.67 191.5 587.61 191.5 589.94C191.5 591.83 189.83 593.21 187.6 593.21H183.59V595.7ZM183.59 591.57H185.87C187.14 591.57 187.72 590.85 187.72 589.91C187.72 589.03 187.15 588.33 185.92 588.33H183.6V591.57H183.59Z"
                    fill="black"
                  />
                  <path
                    d="M198.41 595.7C198.41 596.05 198.65 596.22 198.95 596.22H200.41V597.88H192.65V596.22H194.16C194.44 596.22 194.68 596.06 194.68 595.7V588.85C194.68 588.49 194.44 588.33 194.16 588.33H192.65V586.67H201.49C203.98 586.67 206.32 587.61 206.32 589.94C206.32 591.83 204.65 593.21 202.42 593.21H198.41V595.7ZM198.41 591.57H200.69C201.96 591.57 202.54 590.85 202.54 589.91C202.54 589.03 201.97 588.33 200.74 588.33H198.42V591.57H198.41Z"
                    fill="black"
                  />
                  <path
                    d="M208.99 596.22C209.27 596.22 209.51 596.06 209.51 595.7V588.93C209.51 588.51 209.23 588.33 208.91 588.33H207.48V586.67H221.21V591.12H219.42C219.37 589.97 219.36 588.32 217.57 588.32H213.26V591.22H214.79C215.37 591.22 215.78 590.91 215.78 590.34V589.53H217.52V594.61H215.78V593.81C215.78 593.1 215.45 592.92 214.8 592.92H213.25V596.22H217.72C219.74 596.22 219.54 593.86 219.59 593.02H221.41V597.88H207.47V596.22H208.98H208.99Z"
                    fill="black"
                  />
                  <path
                    d="M229.02 595.86C229.02 596.06 229.28 596.22 229.57 596.22H231.03V597.88H223.26V596.22H224.77C225.05 596.22 225.29 596.06 225.29 595.86V588.69C225.29 588.49 225.05 588.34 224.77 588.34H223.26V586.68H232.6C234.91 586.68 237.17 587.18 237.17 589.48C237.17 591.51 235.38 592.07 233.3 592.2C234.88 592.47 235.84 592.78 236.03 594.28C236.11 594.96 236.08 596.23 236.65 596.23C237.02 596.23 237.14 595.97 237.14 595.47V594.32H238.85V595.36C238.85 597.68 237.82 598.14 235.43 598.14C233.98 598.14 232.84 597.59 232.63 596.17L232.32 594.07C232.26 593.65 232.04 593.03 230.95 593.03H229.02V595.88V595.86ZM229.02 591.33H230.99C232.3 591.33 233.25 591.17 233.25 589.75C233.25 588.8 232.54 588.34 231.38 588.34H229.03V591.33H229.02Z"
                    fill="black"
                  />
                </g>
              </svg>

              {/* CTA content directly under the 15 badge */}
              <div className="mt-8 text-center">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                  {t('readyToMakeDifference')}
                </h2>
                <p className="mt-4 text-lg max-w-2xl mx-auto text-white/80">
                  {t('ctaDescription')}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                  <Button size="lg" asChild className="bg-white text-dgr-gold hover:bg-white/90">
                    <Link href="/donate" prefetch={false}>
                      {t('makeADonation')}
                    </Link>
                  </Button>
                  <Button size="lg" asChild className="bg-black text-white hover:bg-black/80">
                    <Link href="/register" prefetch={false}>
                      {tNav('register')}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 15 Years of Riding Dapper - Scroll Animation (WIP - hidden until ready)
      <section ref={rideRef} className="relative bg-stone-950">
          <div className="h-[350vh] md:h-[250vh]">
            <div className="sticky top-0 h-screen overflow-hidden">
              <div className="absolute top-4 md:top-10 left-0 right-0 text-center z-10 px-4">
                <p className="text-dgr-gold text-xs md:text-sm uppercase tracking-[0.2em] font-medium mb-1 md:mb-2">
                  2012 — 2026
                </p>
                <h2 className="text-xl md:text-5xl font-bold text-white">
                  15 Years of <span className="text-dgr-gold">Riding Dapper</span>
                </h2>
              </div>

              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                fill="none"
              >
                <path
                  d={isMobile ? ROAD_PATH_MOBILE : ROAD_PATH_DESKTOP}
                  stroke="hsl(var(--dgr-gold) / 0.06)"
                  strokeWidth={isMobile ? '6' : '4'}
                  fill="none"
                />
                <path
                  ref={pathRef}
                  d={isMobile ? ROAD_PATH_MOBILE : ROAD_PATH_DESKTOP}
                  stroke="hsl(var(--dgr-gold) / 0.15)"
                  strokeWidth="0.4"
                  strokeDasharray="1.5 1.5"
                  fill="none"
                />
              </svg>

              <div ref={bikeRef} className="absolute w-10 h-5 md:w-24 md:h-12 z-20">
                <MotorcycleSilhouette />
              </div>

              {MILESTONES.map((milestone, i) => {
                const pos = isMobile ? milestone.mobile : milestone.desktop;
                return (
                  <div
                    key={milestone.year}
                    ref={(el) => {
                      milestoneRefs.current[i] = el;
                    }}
                    className="absolute z-10"
                    style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                  >
                    <div className="relative flex flex-col items-center -translate-x-1/2">
                      <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-dgr-gold bg-stone-950/80 backdrop-blur-sm mb-2">
                        <span className="text-dgr-gold text-xs md:text-sm font-bold">
                          {milestone.year}
                        </span>
                      </div>
                      <div className="bg-stone-900/90 backdrop-blur-sm border border-stone-700/50 rounded-lg overflow-hidden max-w-[150px] md:max-w-[200px]">
                        <div className="relative w-full h-16 md:h-24">
                          <Image
                            src={milestone.image}
                            alt={milestone.title}
                            fill
                            className="object-cover"
                            sizes="200px"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent" />
                        </div>
                        <div className="px-3 py-2 md:px-4 md:py-3 text-center">
                          <p className="text-white text-xs md:text-sm font-semibold leading-tight">
                            {milestone.title}
                          </p>
                          <p className="text-stone-400 text-[10px] md:text-xs mt-1 leading-tight">
                            {milestone.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="absolute bottom-4 md:bottom-6 left-0 right-0 text-center">
                <p className="text-stone-600 text-xs animate-bounce">↓ scroll</p>
              </div>
            </div>
          </div>
        </section>
      */}
    </>
  );
}
